import { Component } from 'preact'
import DynamicSaveBar from '../../ui-components/DynamicSaveBar'
import InputGroup from '../../ui-components/InputGroup'
import IncludeForStats from '../../category/control/IncludeForStats'
import Attachments from '../../category/control/Attachments'
import SeoSettings from '../../category/control/SeoSettings'
import axios from 'axios'
import ConfirmationModal from '../../ui-components/ConfirmationModal'
import tracker from '../../../components/tracker/publish'

const eventTracker = (category) => {
  tracker.trackEvent('userevent', {
    type: 'Category Page Saved',
    category_title: category.title,
    category_type: 'knowledgebase',
    category_has_hero_image: category.heroImage !== null
  })
}

class KnowledgeBaseCategoryDetails extends Component {
  constructor (props) {
    super()
    this.state = {
      errors: [],
      categoryDetails: props.categoryDetails,
      showModal: false,
      showConfirmation: false,
      isLoading: false
    }
    this.savedCategoryDetails = props.categoryDetails
    this.saveCategory = this.saveCategory.bind(this)
    this.resetChanges = this.resetChanges.bind(this)
    this.setValue = this.setValue.bind(this)
    this.deleteCategory = this.deleteCategory.bind(this)
    this.getDeleteConfirmationTitle = this.getDeleteConfirmationTitle.bind(this)
    this.getDeleteConfirmationText = this.getDeleteConfirmationText.bind(this)
    this.getDeleteConfirmationButtons = this.getDeleteConfirmationButtons.bind(this)
    this.closeConfirmationDialog = this.closeConfirmationDialog.bind(this)
  }

  deleteCategory () {
    this.setState({ isLoading: true })
    axios.delete('/platform/knowledge-base/edit', { params: { id: this.state.categoryDetails.id } })
      .then(() => {
        this.props.showMessage('success')
        this.savedCategoryDetails = this.state.categoryDetails
        window.location.assign(this.props.overviewLink)
      })
      .catch((e) => {
        this.props.showMessage('error')
      })
  }

  canBeDeleted () {
    return this.state.categoryDetails.visibleTopics === 0
  }

  closeConfirmationDialog () {
    this.setState({ showConfirmation: false })
  }

  getDeleteConfirmationTitle () {
    if (this.canBeDeleted()) {
      return `Delete category "${this.state.categoryDetails.title}"`
    }

    return 'Category is not empty yet'
  }

  getDeleteConfirmationText () {
    if (this.canBeDeleted()) {
      return "Be careful, once it's deleted, it's gone for good!"
    }

    return <div>
      If you're absolutely sure you want to delete it, you'll need to move all of its <strong>{this.state.categoryDetails.visibleTopics} topics</strong> a different category or delete them.
    </div>
  }

  getDeleteConfirmationButtons () {
    if (!this.canBeDeleted()) {
      return [{
        text: 'Okay',
        callback: this.closeConfirmationDialog,
        class: 'btn btn-primary',
        showLoading: false
      }]
    }
    return [
      {
        text: 'Delete',
        callback: this.deleteCategory,
        class: 'btn btn-danger',
        showLoading: true
      },
      {
        text: 'Cancel',
        callback: this.closeConfirmationDialog,
        class: 'btn btn-default',
        showLoading: false
      }
    ]
  }

  resetChanges () {
    this.setState({ categoryDetails: this.savedCategoryDetails })
  }

  saveCategory () {
    let errors = []

    if (!this.state.categoryDetails.title || this.state.categoryDetails.title === '') {
      errors.push('title')
    }
    if (!this.state.categoryDetails.description || this.state.categoryDetails.description === '') {
      errors.push('description')
    }
    if (errors.length) {
      this.setState({ errors })
      return
    }
    this.setState({ isLoading: true })

    axios.post('/platform/knowledge-base/edit', this.state.categoryDetails, { params: { id: this.state.categoryDetails.id } })
      .then(() => {
        this.savedCategoryDetails = this.state.categoryDetails
        this.setState({ isLoading: false })
        eventTracker(this.state.categoryDetails)
        this.props.showMessage('success')
        this.props.updateCategory(this.state.categoryDetails)
      })
      .catch((e) => {
        this.setState({ isLoading: false })
        this.props.showMessage('error')
      })
  }

  setValue (key, value) {
    const categoryDetails = Object.assign({}, this.state.categoryDetails, { [key]: value })
    this.setState({ categoryDetails, errors: [] })
  }

  hasChanged () {
    return JSON.stringify(this.savedCategoryDetails) !== JSON.stringify(this.state.categoryDetails)
  }

  componentDidUpdate () {
    this.props.hasTabChanges(this.hasChanged())
  }

  componentDidMount () {
    this.props.saveUpdates(this.saveCategory)
    this.props.discardChanges(this.resetChanges)
  }

  render (props, state) {
    const { title, description, includeForStats, heroImage, thumbnailImage, seoTitle, seoDescription, metaRobots, hideFromSearch } = state.categoryDetails
    const displaySaveBar = this.hasChanged()

    return (
      <div className='category-details'>
        <form>
          <InputGroup
            title='Title'
            placeholder='Add a title'
            type={'text'}
            error={state.errors.indexOf('title') !== -1 ? 'Please fill in this field' : ''}
            onChange={(e) => this.setValue('title', e.target.value)}
            value={title} />
          <InputGroup
            title='Description'
            placeholder='Add a description'
            error={state.errors.indexOf('description') !== -1 ? 'Please fill in this field' : ''}
            type={'text'}
            onChange={(e) => this.setValue('description', e.target.value)}
            value={description} />
          <SeoSettings
            seoTitle={seoTitle || title}
            seoDescription={seoDescription || description}
            onChangeTitle={(value) => this.setValue('seoTitle', value)}
            hideFromSearch={hideFromSearch === undefined ? false : hideFromSearch}
            onChangeHideFromSearch={(value) => this.setValue('hideFromSearch', value)}
            onChangeDescription={(value) => this.setValue('seoDescription', value)}
            onChangeMetaRobot={(value) => this.setValue('metaRobots', value)}
            metaRobots={metaRobots || 'index, follow'}
            displayRobotsSettings
          />
          <IncludeForStats
            enable={includeForStats}
            onUpdate={(value) => this.setValue('includeForStats', value)}
          />
          <Attachments
            label='Hero image'
            className='hero-image'
            meta={
              <div>
                <div>Hero image will be shown on top of the category page.</div>
                <div>Maximum file size: 5MB. Allowed image types: .jpg, .png, .gif. We recommend a minimum of 2880*480px</div>
              </div>
            }
            isAttachmentSelected={heroImage}
            imageSrc={heroImage && heroImage.source_thumb}
            width={2880}
            height={480}
            onUpdate={(data) => this.setValue('heroImage', data)}
          />
          <Attachments
            label='Thumbnail image'
            className='thumbnail-image'
            meta={
              <div>
                <div>Allowed image types: .jpg, .png, .gif. We recommend a minimum of 144*144px</div>
              </div>
            }
            isAttachmentSelected={thumbnailImage}
            imageSrc={thumbnailImage && thumbnailImage.source_thumb}
            width={144}
            height={144}
            onUpdate={(data) => this.setValue('thumbnailImage', data)}
          />
        </form>
        <div className='btn btn-danger btn-delete'
          onClick={() => this.setState({ showConfirmation: true })}>
          Delete Category
        </div>
        <ConfirmationModal
          isLoading={state.isLoading}
          enableModal={state.showConfirmation}
          title={this.getDeleteConfirmationTitle()}
          message={this.getDeleteConfirmationText()}
          buttons={this.getDeleteConfirmationButtons()}
          onClose={this.closeConfirmationDialog}
        />
        {
          displaySaveBar ? (
            <DynamicSaveBar
              isLoading={state.isLoading}
              cancelCallback={this.resetChanges}
              saveCallback={this.saveCategory} />
          ) : null
        }
      </div>
    )
  }
}

export default KnowledgeBaseCategoryDetails
